import { ConnectError } from '@connectrpc/connect';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import api from '@/shared/api/api';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Button, Checkbox, errorToastCaller, Modal } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { userModel } from '@/entities/user';

import { useLogout } from '@/features/logout';

import { i18n } from '../../config/i18n';

import styles from './FDADentalProfessionalConfirmationModal.module.scss';

export const FDADentalProfessionalConfirmationModal: FC = () => {
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();
  const logout = useLogout();

  const { visible } = useAppSelector(
    (state) => state.modal.FDADentalProfessionalConfirmationModal,
  );

  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked((prev) => !prev);
  };

  const handleCancel = () => {
    dispatch(
      modalModel.actions.closeModal(
        ModalID.FDADentalProfessionalConfirmationModal,
      ),
    );
    logout();
  };

  const handleContinue = async () => {
    setIsLoading(true);

    try {
      const response = await api.user.confirmFDADentalProfessional({});

      if (response.User) {
        dispatch(userModel.actions.setOne(response.User));
      }

      dispatch(
        modalModel.actions.closeModal(
          ModalID.FDADentalProfessionalConfirmationModal,
        ),
      );
    } catch (error) {
      errorToastCaller(error as ConnectError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      data-component="fda-confirmation-modal"
      isOpen={visible}
      shouldRenderCloseIconButton={false}
      shouldCloseOnOverlayClick={false}
      hideFooter
      borderless
    >
      <div className={styles.container}>
        <h3 className={cn('h3', styles.title)}>
          <FormattedMessage {...i18n.title} />
        </h3>

        <div className={styles.content}>
          <p className={cn('p2', styles.description)}>
            <FormattedMessage {...i18n.intendedUse} />
          </p>

          <p className={cn('p2', styles.description)}>
            <FormattedMessage {...i18n.adjunctiveAid} />
          </p>

          <p className={cn('p2', styles.prompt)}>
            <FormattedMessage {...i18n.pleaseConfirm} />
          </p>

          <Checkbox checked={isChecked} onChange={handleCheckboxChange}>
            <FormattedMessage {...i18n.checkboxLabel} />
          </Checkbox>
        </div>

        <div className={styles.actions}>
          <Button
            variant="tertiary"
            onClick={handleCancel}
            size={isMobile ? 'medium' : 'large'}
          >
            <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
          </Button>

          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={!isChecked}
            loading={isLoading}
            size={isMobile ? 'medium' : 'large'}
          >
            <FormattedMessage id="global.continue" defaultMessage="Continue" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};
